import { lazy, Suspense, useCallback, useEffect, useMemo, useState, useRef } from "react";
import {
  Activity,
  Menu,
  ArrowUpRight,
  Layers,
  AlertTriangle,
  CheckCircle2,
  Database,
  Edit3,
  Eye,
  LogOut,
  Mail,
  Plus,
  RefreshCw,
  Save,
  Search,
  Settings as SettingsIcon,
  Shield,
  Trash2,
  Users,
  XCircle,
} from "lucide-react";
import {
  ADMIN_UNAUTHORIZED_EVENT,
  api,
  getErrorMessage,
} from "./api/client";

import Login from "./AdminLogin";
import AdminReauthentication from "./AdminReauthentication";
import AdminNavigation, { adminViews, adminSections } from "./AdminNavigation";
const AnalyticsReports = lazy(() => import("./AnalyticsReports"));
const FinancePanel = lazy(() => import("./FinancePanel"));
const EmailLogs = lazy(() => import("./EmailLogs"));
const SystemBlueprint = lazy(() => import("./SystemBlueprint"));
const UserDetails = lazy(() => import("./UserDetails"));
const OperationsPanel = lazy(() => import("./OperationsPanel"));

const BillingPanel = lazy(() => import("./BillingPanel"));
const StorageSettings = lazy(() => import("./StorageSettings"));
const EmailPanel = lazy(() => import("./EmailPanel"));

const LazyPanel = ({ children }) => (
  <Suspense fallback={<section className="panel"><p>Loading…</p></section>}>
    {children}
  </Suspense>
);

const emptyDashboard = {
  stats: { users: 0, tools: 0, contacts: 0, subscriptions: 0, errors: 0, authOtps: 0 },
  recentContacts: [],
  recentErrors: [],
  settings: [],
};

const formatDate = (value) => (value ? new Date(value).toLocaleString() : "-");

const formatValue = (value, field = {}) => {
  if (value == null || value === "") return "-";
  if (field.type === "datetime") return formatDate(value);
  if (typeof value === "object") return JSON.stringify(value, null, 2);
  return String(value);
};

const errorDetailFields = [
  { name: "message", label: "Message", type: "textarea" },
  { name: "statusCode", label: "Status", type: "number" },
  { name: "route", label: "Route", type: "text" },
  { name: "method", label: "Method", type: "text" },
  { name: "requestId", label: "Request ID", type: "text" },
  { name: "details", label: "Details", type: "json" },
  { name: "body", label: "Body", type: "json" },
  { name: "query", label: "Query", type: "json" },
  { name: "params", label: "Params", type: "json" },
  { name: "stack", label: "Stack Trace", type: "textarea" },
  { name: "userAgent", label: "User Agent", type: "textarea" },
  { name: "ip", label: "IP", type: "text" },
  { name: "createdAt", label: "Created", type: "datetime" },
  { name: "updatedAt", label: "Updated", type: "datetime" },
];

const statusTone = (statusCode) => {
  const status = Number(statusCode);
  if (status >= 500) return "danger";
  if (status >= 400) return "warn";
  return "neutral";
};

const getTableFields = (config) => {
  if (config?.key === "errorlogs") {
    return config.fields.filter((field) =>
      ["message", "statusCode", "route", "method", "requestId"].includes(field.name)
    );
  }
  if (config?.key === "authotps") {
    return config.fields.filter((field) =>
      ["email", "otp", "purpose", "attempts", "verifiedAt", "consumedAt"].includes(field.name)
    );
  }
  return (config?.fields || []).slice(0, 5);
};

const StatCard = ({ icon: Icon, label, value, tone = "blue" }) => (
  <div className="stat-card">
    <div className={`stat-icon ${tone}`}><Icon size={20} /></div>
    <div>
      <p>{label}</p>
      <strong>{value ?? "—"}</strong>
    </div>
  </div>
);

const EmptyState = ({ title, message }) => (
  <div className="empty-state">
    <Database size={22} />
    <strong>{title}</strong>
    <span>{message}</span>
  </div>
);

function ErrorDetailGrid({ item, fields = errorDetailFields }) {
  return (
    <div className="detail-grid error-detail-grid">
      {fields.map((field) => (
        <div className={["details", "body", "query", "params", "stack", "message", "userAgent"].includes(field.name) ? "detail-row wide" : "detail-row"} key={field.name}>
          <strong>{field.label}</strong>
          <pre>{formatValue(item?.[field.name], field)}</pre>
        </div>
      ))}
      <div className="detail-row wide">
        <strong>Document ID</strong>
        <pre>{item?._id || "-"}</pre>
      </div>
    </div>
  );
}

function ErrorLogRow({ item, onView, selected, onToggle, onDelete, selectable = false }) {
  return (
    <article className={`log-row ${selectable ? "selectable" : ""}`} role="button" tabIndex={0} onClick={() => onView(item)} onKeyDown={(event) => event.key === "Enter" && onView(item)}>
      {selectable && (
        <input
          type="checkbox"
          checked={selected}
          aria-label={`Select log ${item._id}`}
          onChange={() => onToggle(item._id)}
          onClick={(event) => event.stopPropagation()}
        />
      )}
      <span className={`status-badge ${statusTone(item.statusCode)}`}>{item.statusCode || "ERR"}</span>
      <div className="log-row-main">
        <strong>{item.message || "Unhandled error"}</strong>
        <span>{item.method || "-"} {item.route || "Unknown route"}</span>
      </div>
      <span className="log-row-meta">{item.requestId || item.ip || "-"}</span>
      <time>{formatDate(item.createdAt)}</time>
      {onDelete && (
        <button className="icon-button danger" type="button" onClick={(event) => { event.stopPropagation(); onDelete(item._id); }} title="Delete log">
          <Trash2 size={16} />
        </button>
      )}
    </article>
  );
}

function ErrorLogPanel({
  title,
  items,
  onView,
  onOpenAll,
  embedded = false,
  selectedIds = [],
  onToggle,
  onDelete,
  selectable = false,
}) {
  const selectedSet = new Set(selectedIds);
  return (
    <section className={embedded ? "error-panel embedded-error-panel" : "panel error-panel"}>
      <div className="panel-heading compact-heading">
        <div>
          <h2>{title}</h2>
          <p>Click a log to view details.</p>
        </div>
        {onOpenAll && <button className="secondary-button" type="button" onClick={onOpenAll}><Database size={16} /> All errors</button>}
      </div>
      <div className="error-list">
        {items.length ? items.map((item) => (
          <ErrorLogRow
            key={item._id}
            item={item}
            onView={onView}
            selected={selectedSet.has(item._id)}
            onToggle={onToggle}
            onDelete={onDelete}
            selectable={selectable}
          />
        )) : (
          <EmptyState title="No errors logged" message="Captured backend errors will appear here." />
        )}
      </div>
    </section>
  );
}

const ActivityList = ({ title, items, type }) => (
  <section className="panel activity-panel">
    <div className="panel-heading compact-heading">
      <div>
        <h2>{title}</h2>
        <p>Latest operational records</p>
      </div>
    </div>
    <div className="activity-list">
      {items.length ? items.map((item) => (
        <div className="activity-item" key={item._id}>
          <div className={`activity-marker ${type === "error" || item.status === "failed" ? "danger" : "success"}`}>
            {type === "error" || item.status === "failed" ? <XCircle size={16} /> : <CheckCircle2 size={16} />}
          </div>
          <div>
            <strong>{type === "error" ? item.message || "Unhandled error" : item.email || item.name || "Contact"}</strong>
            <span>{type === "error" ? item.route || item.method || "Unknown route" : item.message || "Contact submission"}</span>
          </div>
          <time>{formatDate(item.createdAt)}</time>
        </div>
      )) : <EmptyState title="No records yet" message="Recent activity will appear here." />}
    </div>
  </section>
);


function SettingsPanel({ settings, onSave }) {
  const [drafts, setDrafts] = useState({});
  const [savingId, setSavingId] = useState("");
  const [notice, setNotice] = useState("");

  useEffect(() => {
    setDrafts(Object.fromEntries(settings.map((setting) => [setting._id, setting.value ?? ""])));
  }, [settings]);

  const grouped = settings.reduce((acc, setting) => {
    const group = setting.group || "General";
    acc[group] = acc[group] || [];
    acc[group].push(setting);
    return acc;
  }, {});

  const save = async (setting, value) => {
    setSavingId(setting._id);
    setNotice("");
    try {
      await onSave(setting._id, value);
      setNotice(`${setting.label} saved.`);
    } catch {
      // The workspace-level alert is set by onSave.
    } finally {
      setSavingId("");
    }
  };

  return (
    <section className="panel">
      <div className="panel-heading">
        <div>
          <h2>Platform controls</h2>
          <p>Manage access, notifications, observability, and platform behavior.</p>
        </div>
      </div>
      {notice && <div className="notice success"><CheckCircle2 size={17} /> {notice}</div>}
      <div className="toggle-grid">
        {settings.length ? Object.entries(grouped).map(([group, groupSettings]) => (
          <div className="settings-group" key={group}>
            <h3>{group}</h3>
            {groupSettings.map((setting) => (
              <div className="toggle-row setting-row" key={setting._id}>
                <div>
                  <strong>{setting.label}<code>{setting.key}</code></strong>
                  <span>{setting.description}</span>
                  <small>{setting.type} · {setting.group || "General"}</small>
                </div>
                {setting.type === "boolean" ? (
                  <button
                    className={`switch-control ${Boolean(drafts[setting._id]) ? "on" : ""}`}
                    type="button"
                    onClick={() => save(setting, !Boolean(drafts[setting._id]))}
                    disabled={savingId === setting._id}
                    aria-pressed={Boolean(drafts[setting._id])}
                  >
                    <span />
                    {Boolean(drafts[setting._id]) ? "On" : "Off"}
                  </button>
                ) : (
                  <div className="setting-edit">
                    {setting.type === "json" ? (
                      <textarea
                        value={typeof drafts[setting._id] === "string" ? drafts[setting._id] : JSON.stringify(drafts[setting._id] ?? "", null, 2)}
                        onChange={(event) => setDrafts({ ...drafts, [setting._id]: event.target.value })}
                      />
                    ) : (
                      <input
                        type={setting.type === "number" ? "number" : "text"}
                        value={drafts[setting._id] ?? ""}
                        onChange={(event) => setDrafts({ ...drafts, [setting._id]: event.target.value })}
                        placeholder={setting.type === "string" ? "Enter value" : ""}
                      />
                    )}
                    <button className="primary-button compact" type="button" disabled={savingId === setting._id} onClick={() => save(setting, drafts[setting._id])}>
                      <Save size={14} /> {savingId === setting._id ? "Saving..." : "Save"}
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )) : <EmptyState title="No controls available" message="Settings are seeded by the backend when available." />}
      </div>
    </section>
  );
}

function SettingsWorkspace({ settings, onSave }) {
  const [section, setSection] = useState("platform");
  const sections = [
    { key: "platform", label: "Platform", icon: SettingsIcon },
    { key: "email", label: "Email & OTP", icon: Mail },
    { key: "billing", label: "Billing & access", icon: Shield },
    { key: "storage", label: "Storage & backups", icon: Database },
  ];

  return (
    <div className="settings-workspace">
      <section className="settings-hero">
        <div>
          <span className="eyebrow">Configuration</span>
          <h2>Settings</h2>
          <p>Manage platform behavior and integrations from one place.</p>
        </div>
      </section>
      <nav className="settings-tabs" aria-label="Settings sections">
        {sections.map(({ key, label, icon: Icon }) => (
          <button type="button" key={key} className={section === key ? "active" : ""} onClick={() => setSection(key)}>
            <Icon size={17} /> {label}
          </button>
        ))}
      </nav>
      {section === "storage" && <LazyPanel><StorageSettings /></LazyPanel>}
      {section === "platform" && <SettingsPanel settings={settings} onSave={onSave} />}
      {section === "email" && <LazyPanel><EmailPanel /></LazyPanel>}
      {section === "billing" && <LazyPanel><BillingPanel showSettings showOperations={false} /></LazyPanel>}
    </div>
  );
}

function DashboardActions({ onNavigate, onOpenErrors }) {
  return (
    <section className="panel quick-actions-panel">
      <div className="panel-heading compact-heading">
        <div><h2>Quick actions</h2><p>Common operational tasks.</p></div>
      </div>
      <div className="quick-actions-grid">
        <button type="button" onClick={() => onNavigate("billing")}><Shield size={19} /><span><strong>Manage subscriptions</strong><small>Grant, review, or revoke access</small></span></button>
        <button type="button" onClick={() => onNavigate("settings")}><SettingsIcon size={19} /><span><strong>Open settings</strong><small>Platform, SMTP, pricing, and access</small></span></button>
        <button type="button" onClick={onOpenErrors}><AlertTriangle size={19} /><span><strong>Review errors</strong><small>Inspect recent backend failures</small></span></button>
      </div>
    </section>
  );
}

function Reports({ reports }) {
  const errors = reports?.errorsByRoute || [];
  const subscriptions = reports?.dailySubscriptions || [];
  const contacts = reports?.dailyContacts || [];

  return (
    <section className="panel">
      <div className="panel-heading">
        <div>
          <h2>Reports</h2>
          <p>Last 30 days activity and error concentration.</p>
        </div>
      </div>
      <div className="report-grid">
        <div>
          <h3>Contacts</h3>
          {contacts.length ? contacts.map((item) => <p key={item._id}><span>{item._id}</span><b>{item.count}</b></p>) : <em>No contact activity</em>}
        </div>
        <div>
          <h3>Subscriptions</h3>
          {subscriptions.length ? subscriptions.map((item) => <p key={item._id}><span>{item._id}</span><b>{item.count}</b></p>) : <em>No subscription activity</em>}
        </div>
        <div>
          <h3>Error Routes</h3>
          {errors.length ? errors.map((item) => <p key={item._id || "unknown"}><span>{item._id || "unknown"}</span><b>{item.count}</b></p>) : <em>No errors logged</em>}
        </div>
      </div>
    </section>
  );
}

function DataTable({ collection, collections }) {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [editing, setEditing] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [form, setForm] = useState({});
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const config = collections.find((item) => item.key === collection);

  const visibleFields = useMemo(() => config?.fields || [], [config]);
  const tableFields = useMemo(() => getTableFields(config), [config]);

  const load = useCallback(async () => {
    if (!collection) return;
    setError("");
    setLoading(true);
    try {
      const { data } = await api.get(`/collections/${collection}`, { params: { page, search } });
      setItems(data.items);
      setTotal(data.total);
      setSelectedIds((ids) => ids.filter((id) => data.items.some((item) => item._id === id)));
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [collection, page, search]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    setSelectedIds([]);
  }, [collection]);

  const startCreate = () => {
    setEditing({ mode: "create" });
    setForm({});
  };

  const startEdit = (item) => {
    setEditing({ mode: "edit", id: item._id });
    setForm(item);
  };

  const save = async (event) => {
    event.preventDefault();
    const payload = Object.fromEntries(
      visibleFields.map((field) => [field.name, form[field.name]]).filter(([, value]) => value !== undefined)
    );
    try {
      if (editing.mode === "create") await api.post(`/collections/${collection}`, payload);
      else await api.patch(`/collections/${collection}/${editing.id}`, payload);
      setEditing(null);
      setForm({});
      await load();
    } catch (err) {
      setError(getErrorMessage(err));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this record?")) return;
    try {
      await api.delete(`/collections/${collection}/${id}`);
      setSelectedIds((ids) => ids.filter((selectedId) => selectedId !== id));
      await load();
    } catch (err) {
      setError(getErrorMessage(err));
    }
  };

  const toggleSelected = (id) => {
    setSelectedIds((ids) => ids.includes(id) ? ids.filter((selectedId) => selectedId !== id) : [...ids, id]);
  };

  const toggleAllVisible = () => {
    const visibleIds = items.map((item) => item._id);
    const allVisibleSelected = visibleIds.length && visibleIds.every((id) => selectedIds.includes(id));
    setSelectedIds(allVisibleSelected ? selectedIds.filter((id) => !visibleIds.includes(id)) : [...new Set([...selectedIds, ...visibleIds])]);
  };

  const removeSelected = async () => {
    if (!selectedIds.length) return;
    if (!window.confirm(`Delete ${selectedIds.length} selected log${selectedIds.length === 1 ? "" : "s"}?`)) return;
    try {
      await api.post(`/collections/${collection}/bulk-delete`, { ids: selectedIds });
      setSelectedIds([]);
      await load();
    } catch (err) {
      setError(getErrorMessage(err));
    }
  };

  if (!config) return null;
  const isErrorLogs = config.key === "errorlogs";
  const canDelete = !config.readOnly || config.allowDelete;
  const canBulkDelete = Boolean(config.allowBulkDelete);
  const allVisibleSelected = Boolean(items.length) && items.every((item) => selectedIds.includes(item._id));

  return (
    <section className="panel data-panel">
      <div className="panel-heading">
        <div>
          <h2>{config.label}</h2>
          <p>{total} records {loading ? "loading..." : ""}</p>
        </div>
        <div className="toolbar">
          <div className="search-box">
            <Search size={16} />
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              onKeyDown={(event) => event.key === "Enter" && load()}
              placeholder="Search"
            />
          </div>
          <button className="icon-button" onClick={load} title="Refresh" disabled={loading}><RefreshCw size={17} /></button>
          {isErrorLogs && canBulkDelete && (
            <>
              <button className="secondary-button" type="button" onClick={toggleAllVisible} disabled={!items.length}>
                {allVisibleSelected ? "Clear" : "Select page"}
              </button>
              <button className="secondary-button danger" type="button" onClick={removeSelected} disabled={!selectedIds.length}>
                <Trash2 size={16} /> Delete selected
              </button>
            </>
          )}
          {!config.readOnly && <button className="primary-button compact" onClick={startCreate}><Plus size={16} /> Add</button>}
        </div>
      </div>
      {error && <div className="alert">{error}</div>}
      {isErrorLogs ? (
        <ErrorLogPanel
          title="Error Logs"
          items={items}
          onView={setViewing}
          embedded
          selectedIds={selectedIds}
          onToggle={toggleSelected}
          onDelete={canDelete ? remove : undefined}
          selectable={canDelete}
        />
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                {tableFields.map((field) => <th key={field.name}>{field.label}</th>)}
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item._id}>
                  {tableFields.map((field) => (
                    <td key={field.name} className={field.name === "statusCode" ? `status-cell status-${item[field.name]}` : ""}>
                      {formatValue(item[field.name], field)}
                    </td>
                  ))}
                  <td>{formatDate(item.createdAt)}</td>
                  <td>
                    <div className="row-actions">
                      <button className="icon-button" onClick={() => setViewing(item)} title="View"><Eye size={16} /></button>
                      {!config.readOnly && <button className="icon-button" onClick={() => startEdit(item)} title="Edit"><Edit3 size={16} /></button>}
                      {canDelete && <button className="icon-button danger" onClick={() => remove(item._id)} title="Delete"><Trash2 size={16} /></button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {!loading && !items.length && (
        <EmptyState
          title="No records found"
          message={search ? "Try another search term or clear the filter." : "Records will appear here when this collection has data."}
        />
      )}
      <div className="pager">
        <button disabled={page === 1} onClick={() => setPage(page - 1)}>Previous</button>
        <span>Page {page}</span>
        <button disabled={page * 20 >= total} onClick={() => setPage(page + 1)}>Next</button>
      </div>
      {editing && (
        <div className="modal-backdrop" role="presentation">
          <form className="modal" onSubmit={save}>
            <div className="modal-heading">
              <h2>{editing.mode === "create" ? "Add" : "Edit"} {config.label}</h2>
              <button className="icon-button" type="button" onClick={() => setEditing(null)} title="Close"><XCircle size={17} /></button>
            </div>
            {visibleFields.filter((field) => editing.mode === "create" || !field.createOnly).map((field) => (
              <label key={field.name}>
                {field.label}
                {field.type === "textarea" ? (
                  <textarea value={form[field.name] ?? ""} onChange={(event) => setForm({ ...form, [field.name]: event.target.value })} required={field.required} />
                ) : field.type === "select" ? (
                  <select value={form[field.name] ?? field.options?.[0]} onChange={(event) => setForm({ ...form, [field.name]: event.target.value })}>
                    {field.options.map((option) => <option key={option} value={option}>{option}</option>)}
                  </select>
                ) : (
                  <input
                    value={form[field.name] ?? ""}
                    type={field.type === "json" ? "text" : field.type}
                    onChange={(event) => setForm({ ...form, [field.name]: event.target.value })}
                    required={field.required}
                    pattern={field.pattern}
                  />
                )}
              </label>
            ))}
            <div className="modal-actions">
              <button type="button" onClick={() => setEditing(null)}>Cancel</button>
              <button className="primary-button compact" type="submit"><Save size={16} /> Save</button>
            </div>
          </form>
        </div>
      )}
      {viewing && (
        <div className="modal-backdrop" role="presentation">
          <div className="modal detail-modal">
            <div className="modal-heading">
              <h2>{config.label} Details</h2>
              <button className="icon-button" type="button" onClick={() => setViewing(null)} title="Close"><XCircle size={17} /></button>
            </div>
            {config.key === "users" ? <LazyPanel><UserDetails id={viewing._id} /></LazyPanel> : isErrorLogs ? <ErrorDetailGrid item={viewing} /> : (
              <div className="detail-grid">
                {visibleFields.map((field) => (
                  <div className={field.type === "textarea" || field.type === "json" ? "detail-row wide" : "detail-row"} key={field.name}>
                    <strong>{field.label}</strong>
                    <pre>{formatValue(viewing[field.name], field)}</pre>
                  </div>
                ))}
                <div className="detail-row wide">
                  <strong>Document ID</strong>
                  <pre>{viewing._id}</pre>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
}

function AdminShell({ admin, onLogout }) {
  const readView = () => { const view = new URLSearchParams(window.location.search).get('view'); return adminViews.has(view) ? view : 'dashboard'; };
  const [active, updateActive] = useState(readView);
  const mobileNav = useRef(null);
  const setActive = useCallback(view => {
    if (!adminViews.has(view)) return;
    const url = new URL(window.location.href); url.searchParams.set('view', view);
    if (url.href !== window.location.href) window.history.pushState({}, '', url);
    updateActive(view);
  }, []);
  useEffect(() => { const restore = () => updateActive(readView()); window.addEventListener('popstate', restore); return () => window.removeEventListener('popstate', restore); }, []);
  const [overviewLoading, setOverviewLoading] = useState(true);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [collections, setCollections] = useState([]);
  const [selectedCollection, setSelectedCollection] = useState("users");
  const [dashboard, setDashboard] = useState(emptyDashboard);
  const [reports, setReports] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [viewingError, setViewingError] = useState(null);

  const loadOverview = useCallback(async () => {
    setOverviewLoading(true);
    setLoadError("");
    try {
      const [collectionsResponse, dashboardResponse] = await Promise.all([
        api.get("/collections"),
        api.get("/dashboard"),
      ]);
      setCollections(collectionsResponse.data.collections);
      setDashboard(dashboardResponse.data);
      setUpdatedAt(new Date());
    } catch (err) {
      setLoadError(getErrorMessage(err));
    } finally { setOverviewLoading(false); }
  }, []);

  const loadReports = useCallback(async () => {
    setLoadError("");
    try {
      const { data } = await api.get("/reports");
      setReports(data);
    } catch (err) {
      setLoadError(getErrorMessage(err));
    }
  }, []);

  useEffect(() => {
    loadOverview();
  }, [loadOverview]);

  useEffect(() => {
    if (active === "reports" && !reports) loadReports();
  }, [active, loadReports, reports]);

  const saveSetting = async (id, value) => {
    setLoadError("");
    try {
      await api.patch(`/settings/${id}`, { value });
      const { data } = await api.get("/dashboard");
      setDashboard(data);
    } catch (err) {
      setLoadError(getErrorMessage(err));
      throw err;
    }
  };

  const openAllErrors = () => {
    setSelectedCollection("errorlogs");
    setActive("errors");
  };

  const section = adminSections.find(item => item.key === active);
  const pageMeta = [section?.label || 'Admin', section?.description];

  return (
    <div className="admin-layout">
      <a className="skip-admin" href="#admin-content">Skip to content</a>
      <aside className="desktop-sidebar"><AdminNavigation active={active} navigate={setActive} admin={admin} /></aside>
      <dialog className="mobile-nav-dialog" ref={mobileNav} onClick={event => { if (event.target === event.currentTarget) mobileNav.current.close(); }}><AdminNavigation active={active} navigate={setActive} admin={admin} close={() => mobileNav.current.close()} /></dialog>
      <main id="admin-content">
        <header className="workspace-bar"><button className="icon-button mobile-nav-trigger" aria-label="Open navigation" onClick={() => mobileNav.current.showModal()}><Menu size={20} /></button><span><Layers size={15} /> Workspace <span className="breadcrumb-divider">/</span> <strong>{pageMeta?.[0]}</strong></span><span className="workspace-access"><Shield size={14} /> Admin access</span></header>
        <header className="topbar">
          <div>
            <h1>{pageMeta?.[0] || "Admin"}</h1>
            <p>{pageMeta?.[1]}</p>
          </div>
          <div className="topbar-actions">
            {["dashboard", "reports", "settings"].includes(active) && <button className="secondary-button" disabled={overviewLoading} onClick={() => { loadOverview(); if (active === "reports") loadReports(); }}><RefreshCw size={16} className={overviewLoading ? "spin" : ""} /> Refresh</button>}
            <button className="secondary-button" onClick={onLogout}><LogOut size={16} /> Logout</button>
          </div>
        </header>
        {loadError && <div className="alert">{loadError}</div>}

        {active === "dashboard" && (
          <>
            <section className="overview-intro"><div><span className="eyebrow">Your platform, at a glance</span><h2>ConvertiFile operations</h2><p>Manage conversion tools, support requests, memberships, and platform security.</p></div><button className="secondary-button" onClick={() => setActive('operations')}>Open operations <ArrowUpRight size={17} /></button></section>
            <div className="section-kicker"><h2>Workspace snapshot</h2><span aria-live="polite">{overviewLoading ? 'Updating…' : updatedAt ? `Updated ${updatedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}` : 'Awaiting data'}</span></div>
            <div className="stats-grid" aria-busy={overviewLoading}>
              <StatCard icon={Users} label="Users" value={overviewLoading && !updatedAt ? "—" : dashboard.stats.users} tone="blue" />
              <StatCard icon={Database} label="Contacts" value={overviewLoading && !updatedAt ? "—" : dashboard.stats.contacts} tone="violet" />
              <StatCard icon={Activity} label="Newsletter subscribers" value={overviewLoading && !updatedAt ? "—" : dashboard.stats.subscriptions} tone="amber" />
              <StatCard icon={AlertTriangle} label="Errors" value={overviewLoading && !updatedAt ? "—" : dashboard.stats.errors} tone="red" />
            </div>
            <DashboardActions onNavigate={setActive} onOpenErrors={openAllErrors} />
            <div className="dashboard-grid">
              <ActivityList title="Recent Contacts" items={dashboard.recentContacts} type="contact" />
              <ErrorLogPanel title="Recent Errors" items={dashboard.recentErrors} onView={setViewingError} onOpenAll={openAllErrors} />
            </div>
          </>
        )}

        {active === "data" && (
          <>
            <nav className="tabs">
              {collections.map((collection) => (
                <button
                  key={collection.key}
                  className={selectedCollection === collection.key ? "active" : ""}
                  onClick={() => setSelectedCollection(collection.key)}
                >
                  {collection.label}
                </button>
              ))}
            </nav>
            <DataTable key={selectedCollection} collection={selectedCollection} collections={collections} />
          </>
        )}

        {section?.collection && <DataTable key={section.collection} collection={section.collection} collections={collections} />}
        {active === "billing" && <LazyPanel><BillingPanel showPayments={false} /></LazyPanel>}
        {["sales", "invoices"].includes(active) && <LazyPanel><FinancePanel key={active} receiptsOnly={active === "invoices"} /></LazyPanel>}
        {section?.operation && <LazyPanel><OperationsPanel key={active} initialTab={section.operation} standalone /></LazyPanel>}
        {active === "emails" && <LazyPanel><EmailLogs /></LazyPanel>}
        {active === "design" && <LazyPanel><SystemBlueprint /></LazyPanel>}
        {active === "reports" && <Reports reports={reports} />}
        {active === "analytics-reports" && <LazyPanel><AnalyticsReports/></LazyPanel>}
        {active === "operations" && <LazyPanel><OperationsPanel /></LazyPanel>}
        {active === "settings" && <SettingsWorkspace settings={dashboard.settings} onSave={saveSetting} />}
        {viewingError && (
          <div className="modal-backdrop" role="presentation">
            <div className="modal detail-modal error-modal">
              <div className="modal-heading">
                <h2>Error Details</h2>
                <button className="icon-button" type="button" onClick={() => setViewingError(null)} title="Close"><XCircle size={17} /></button>
              </div>
              <ErrorDetailGrid item={viewingError} />
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

export default function App() {
  const version = useRef(0);
  const login = useCallback(value => { version.current++; setAdmin(value); }, []);
  const [admin, setAdmin] = useState(null), [loading, setLoading] = useState(true), [authError, setAuthError] = useState('');
  const authenticated = useRef(false);
  authenticated.current = Boolean(admin);
  const logout = useCallback(async () => {
    version.current++;
    try { await api.post('/auth/logout'); setAdmin(null); }
    catch (error) { if (error.response?.status === 401) setAdmin(null); else setAuthError(getErrorMessage(error)); }
  }, []);
  useEffect(() => {
    let active = true;
    const unauthorized = () => { if (active) { version.current++; setAdmin(null); } };
    const refresh = async (initial = false) => {
      if (!initial && (document.hidden || !authenticated.current)) return;
      const requestedVersion = version.current;
      try { const { data } = await api.get('/me', { skipUnauthorized: true }); if (active && requestedVersion === version.current) setAdmin(data.admin); }
      catch (error) { if (active && requestedVersion === version.current) { if (error.response?.status === 401) setAdmin(null); else setAuthError(getErrorMessage(error)); } }
      finally { if (active) setLoading(false); }
    };
    refresh(true);
    const revalidate = () => refresh();
    window.addEventListener(ADMIN_UNAUTHORIZED_EVENT, unauthorized);
    window.addEventListener('focus', revalidate);
    const timer = setInterval(revalidate, 300000);
    return () => { active = false; clearInterval(timer); window.removeEventListener(ADMIN_UNAUTHORIZED_EVENT, unauthorized); window.removeEventListener('focus', revalidate); };
  }, []);
  if (loading) return <main className="session-loading"><Layers size={30} /><span role="status">Opening your workspace…</span></main>;
  if (!admin) return <Login onLogin={login} />;
  return <>{authError && <p className="alert" role="alert">{authError}</p>}<AdminShell admin={admin} onLogout={logout} /><AdminReauthentication /></>;
}
